
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ClinicalApplications = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-32">

        {/* HERO */}
        <section className="bg-gradient-to-b from-gray-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h1 className="font-serif text-5xl md:text-6xl font-light text-gray-900 mb-6">
              Clinical Applications
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
              Adjunct photobiomodulation and infrared recovery support for musculoskeletal care environments.
            </p>
          </div>
        </section>

        {/* SECTION 1: Post-Operative Recovery Support */}
        <section className="py-16 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-5">
                  <i className="ri-heart-pulse-line text-2xl text-teal-600"></i>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-light text-gray-900 mb-5">
                  Post-Operative Recovery Support
                </h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  Infrared and photobiomodulation systems are commonly studied as adjunct tools during orthopedic rehabilitation. These systems are designed to complement — not replace — standard post-operative care protocols and physical therapy.
                </p>
                <p className="text-gray-700 leading-relaxed mb-5">
                  When integrated alongside established PT and physician-directed care, scheduled infrared sessions may support patient comfort and recovery environment quality. All clinical decisions regarding patient suitability remain with the supervising clinician.
                </p>
                <ul className="space-y-3">
                  {[
                    'Designed for use as an adjunct alongside standard orthopedic rehabilitation',
                    'Commonly scheduled between PT sessions or as a pre-session warm-up protocol',
                    'Non-invasive, patient-supine format suitable for most clinical environments',
                    'Does not replace physician-directed care or physical therapy protocols',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-teal-600 text-base"></i>
                      </div>
                      <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-teal-50 rounded-lg p-8 mt-4 lg:mt-12">
                <h3 className="font-serif text-lg font-medium text-gray-900 mb-4">Common Orthopedic Contexts</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Joint Replacement Rehabilitation', desc: 'Adjunct recovery support during post-surgical PT programs.' },
                    { label: 'Soft Tissue Recovery', desc: 'Commonly studied in musculoskeletal and connective tissue recovery environments.' },
                    { label: 'Spine & Back Rehabilitation', desc: 'Used in clinical settings alongside standard spinal care protocols.' },
                    { label: 'Sports Injury Rehabilitation', desc: 'Integrated into athletic injury recovery programs as a non-invasive adjunct.' },
                  ].map((item, i) => (
                    <div key={i} className="border-l-2 border-teal-400 pl-4">
                      <p className="text-sm font-medium text-gray-900">{item.label}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: Physical Therapy Integration */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="order-2 lg:order-1">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {[
                    {
                      icon: 'ri-arrow-right-up-line',
                      title: 'Pre-PT Priming',
                      desc: 'Sessions scheduled prior to physical therapy to prepare the musculoskeletal environment. Typical session length: 10–20 minutes.',
                    },
                    {
                      icon: 'ri-refresh-line',
                      title: 'Post-PT Recovery Support',
                      desc: 'Adjunct infrared sessions following physical therapy as part of a structured recovery protocol.',
                    },
                    {
                      icon: 'ri-calendar-schedule-line',
                      title: 'Between-Visit Sessions',
                      desc: 'Scheduled recovery sessions between PT appointments to maintain continuity of adjunct support.',
                    },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-6">
                      <div className="w-10 h-10 bg-teal-100 rounded-md flex items-center justify-center mb-4">
                        <i className={`${item.icon} text-xl text-teal-600`}></i>
                      </div>
                      <h4 className="font-medium text-gray-900 text-sm mb-2">{item.title}</h4>
                      <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 bg-gray-900 rounded-lg p-6 text-white">
                  <p className="text-sm text-gray-300 leading-relaxed">
                    <strong className="text-white">Note for clinicians:</strong> Integration protocols should be developed in coordination with supervising physical therapists and physicians. NovaRadiance systems are designed to support — not direct — clinical care decisions.
                  </p>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-5">
                  <i className="ri-body-scan-line text-2xl text-teal-600"></i>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-light text-gray-900 mb-5">
                  Physical Therapy Integration
                </h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  Physical therapy practices commonly integrate photobiomodulation and infrared systems as adjunct tools within their existing care frameworks. These systems are designed to work alongside — not in place of — licensed PT protocols.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Typical session lengths of 10–20 minutes make scheduling practical within standard clinical workflows. Systems may be positioned in dedicated recovery rooms or integrated into existing treatment bay layouts depending on facility configuration.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3: Sports Medicine & Performance Recovery */}
        <section className="py-16 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-5">
                  <i className="ri-run-line text-2xl text-teal-600"></i>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-light text-gray-900 mb-5">
                  Sports Medicine &amp; Performance Recovery
                </h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  Non-invasive infrared recovery support is commonly used in athletic and sports medicine environments as an adjunct to standard training and recovery protocols. These systems are designed for use in professional, collegiate, and clinical sports medicine settings.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Recovery-focused applications may include post-training sessions, between-competition recovery, and adjunct support during injury rehabilitation under the supervision of sports medicine professionals.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-shield-check-line', label: 'Non-Invasive Format' },
                    { icon: 'ri-time-line', label: '10–20 Min Sessions' },
                    { icon: 'ri-group-line', label: 'Team & Individual Use' },
                    { icon: 'ri-stethoscope-line', label: 'Clinician-Supervised' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-md px-4 py-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-lg text-teal-600`}></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4 mt-4 lg:mt-12">
                {[
                  { title: 'Professional Sports Facilities', desc: 'Full-body infrared bed systems designed for high-volume recovery room environments in professional athletic facilities.' },
                  { title: 'Collegiate Athletic Programs', desc: 'Scalable panel and bed configurations suitable for collegiate sports medicine and training room integration.' },
                  { title: 'Sports Medicine Clinics', desc: 'Clinical-grade systems for sports medicine practices offering adjunct recovery support alongside standard care.' },
                  { title: 'Performance Training Centers', desc: 'Recovery room implementation for high-performance training facilities focused on athlete longevity and readiness.' },
                ].map((item, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-5 hover:border-teal-300 transition-colors">
                    <h4 className="font-medium text-gray-900 text-sm mb-1">{item.title}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: Recovery Studios & High-End Gym Recovery Rooms */}
        <section className="py-16 bg-gray-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="order-2 lg:order-1">
                <div className="grid grid-cols-1 gap-4">
                  {[
                    {
                      icon: 'ri-building-2-line',
                      title: 'Dedicated Recovery Studios',
                      desc: 'Full commercial recovery room buildouts featuring full-body infrared bed systems, panel arrays, and facial therapy stations. Designed for high-throughput session scheduling.',
                    },
                    {
                      icon: 'ri-vip-crown-line',
                      title: 'Premium Gym Recovery Rooms',
                      desc: 'High-end fitness facilities increasingly offer dedicated recovery rooms as a member amenity. Our systems are designed for commercial-grade, multi-session daily use.',
                    },
                    {
                      icon: 'ri-hotel-line',
                      title: 'Wellness & Spa Integration',
                      desc: 'Luxury wellness centers and medical spas commonly integrate infrared recovery systems as adjunct offerings alongside existing modalities.',
                    },
                    {
                      icon: 'ri-store-2-line',
                      title: 'Multi-Location Operators',
                      desc: 'Procurement-ready documentation and volume pricing available for multi-location operators. Contact us for facility-scale inquiry.',
                    },
                  ].map((item, i) => (
                    <div key={i} className="bg-white rounded-lg p-5 border border-gray-200 flex gap-4">
                      <div className="w-10 h-10 bg-teal-50 rounded-md flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl text-teal-600`}></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 text-sm mb-1">{item.title}</h4>
                        <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-5">
                  <i className="ri-store-3-line text-2xl text-teal-600"></i>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-light text-gray-900 mb-5">
                  Recovery Studios &amp; High-End Gym Recovery Rooms
                </h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  Commercial recovery room implementation is one of the fastest-growing segments for photobiomodulation and infrared systems. Recovery studios and premium fitness facilities are increasingly incorporating these systems as a core service offering.
                </p>
                <p className="text-gray-700 leading-relaxed mb-5">
                  NovaRadiance systems are designed for commercial-grade, multi-session daily use. Our procurement process includes facility layout guidance, compliance documentation, and ongoing U.S.-based support.
                </p>
                <p className="text-sm text-gray-500 italic">
                  Commercial and professional procurement only. Consumer checkout is not available.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* HOW CLINICS USE IT BOX */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-gray-900 rounded-xl p-10 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-light">How Clinics Use It</h2>
              </div>
              <p className="text-gray-300 text-sm mb-8 max-w-2xl leading-relaxed">
                The following reflects common implementation practices observed in clinical and commercial environments. All protocols should be developed and supervised by qualified clinical staff.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-user-heart-line',
                    title: 'Non-Invasive Format',
                    desc: 'Sessions are non-invasive and require no consumables. Patients remain fully clothed or in minimal attire per facility protocol.',
                  },
                  {
                    icon: 'ri-layout-horizontal-line',
                    title: 'Patient Supine Position',
                    desc: 'Full-body bed systems are designed for patient-supine use. Panel systems may be used in seated or standing configurations depending on the unit.',
                  },
                  {
                    icon: 'ri-calendar-check-line',
                    title: 'Scheduled Sessions',
                    desc: 'Typical session scheduling: 10–20 minutes per session. Frequency and duration determined by supervising clinician based on individual patient considerations.',
                  },
                  {
                    icon: 'ri-eye-off-line',
                    title: 'Eye Protection Protocols',
                    desc: 'Appropriate eye protection should be used during sessions per facility safety protocols. Consult device documentation for specific recommendations.',
                  },
                  {
                    icon: 'ri-file-list-3-line',
                    title: 'Screening Considerations',
                    desc: 'Facilities should establish patient screening protocols prior to use. Contraindication screening is the responsibility of the supervising clinical team.',
                  },
                  {
                    icon: 'ri-team-line',
                    title: 'Staff & Workflow Integration',
                    desc: 'Systems are designed to integrate into existing clinical workflows with minimal staff training requirements. Setup and onboarding support provided.',
                  },
                ].map((item, i) => (
                  <div key={i} className="bg-white/10 rounded-lg p-5">
                    <div className="w-9 h-9 bg-teal-500/20 rounded-md flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-lg text-teal-400`}></i>
                    </div>
                    <h4 className="font-medium text-white text-sm mb-2">{item.title}</h4>
                    <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-light text-gray-900 mb-5">
              Ready to Equip Your Facility?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-3 leading-relaxed">
              Our team provides procurement-ready documentation, facility layout guidance, and U.S.-based support throughout the process.
            </p>
            <p className="text-sm text-gray-500 mb-10">
              Commercial and professional procurement only. Facility pricing provided on inquiry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-block bg-teal-600 text-white px-8 py-3 rounded-md text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Request a Quote
              </Link>
              <Link
                to="/contact"
                className="inline-block border border-gray-300 text-gray-900 px-8 py-3 rounded-md text-sm font-medium hover:border-teal-500 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Schedule a Facility Demo
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default ClinicalApplications;
